/**
 * `record_activation` MCP tool — chat-only activation receipt writer (WP-431
 * final pre-ship fix).
 *
 * Why this exists:
 *   The pb-setup skill's Phase 5 needs a path for chat-only agents to call
 *   `setup.recordActivationReceipt`. Before this tool, the skill body told
 *   chat-only agents to use `commit-entry` — but `commit-entry` writes to the
 *   `entries` table (chain.commitEntry), not to `setup_receipt`. That meant
 *   chat-only users could reach Phase 5 readiness yet never write the
 *   activation receipt, leaving OBJ-32-chat unmeasurable and doneWhen #6 of
 *   WP-431 violated for the chat-only surface.
 *
 * Contract:
 *   - Caller passes the activation evidence (confirmedEntryCount,
 *     entriesAcrossCollections, retrievalDemoConfirmed). The tool HARDCODES
 *     `surfaceCapability='chat-only'` because this tool exists exclusively for
 *     chat-only paths — CLI agents have shell access and call the gateway
 *     directly. Allowing the caller to override surfaceCapability would defeat
 *     the whole purpose of the dual-threshold OBJ-32 metric.
 *   - Calls `setup.recordActivationReceipt` via `kernelMutation` (which routes
 *     through the AKI gateway with `injectSessionAuth: true`, mirroring how
 *     S5's `start_pb` calls `setup.stampMcpOnlySurface`). The gateway injects
 *     `apiKeyId | personId | clerkUserId` server-side; this tool never sees
 *     identity material.
 *   - Returns `{ activated, alreadyActivated, receiptId }` — the
 *     `alreadyActivated` flag is forwarded directly from the mutation so
 *     re-runs short-circuit cleanly (DEC-957 re-run-safe).
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { z } from "zod/v3";
import { kernelMutation } from "../client.js";
import { success, textContent } from "../envelope.js";

export const recordActivationSchema = z.object({
  confirmedEntryCount: z
    .number()
    .int()
    .min(0)
    // DEC-994
    .describe(
      "Confirmed-entry count from the Phase 4 capture loop. The mutation enforces >=10.",
    ),
  entriesAcrossCollections: z
    .number()
    .int()
    .min(0)
    // DEC-994
    .describe(
      "Number of distinct collections those entries span. The mutation enforces >=2 (diversity soft-gate).",
    ),
  retrievalDemoConfirmed: z
    .boolean()
    // BR-141
    .describe(
      "True if the retrieval round-trip ran successfully in Phase 4. The mutation rejects false.",
    ),
});

interface RecordActivationResult {
  activated: boolean;
  alreadyActivated: boolean;
  receiptId: string | null;
}

interface RecordActivationMutationResponse {
  alreadyActivated: boolean;
  receiptId: string;
}

/**
 * WP-484 S2 (§3): extracted request handler — reused by the legacy
 * `record_activation` standalone tool AND the new `orient
 * action=record-activation` (absorbed per §3 core-nouns table). One
 * business-logic path.
 */
export async function handleRecordActivation({
  confirmedEntryCount,
  entriesAcrossCollections,
  retrievalDemoConfirmed,
}: {
  confirmedEntryCount: number;
  entriesAcrossCollections: number;
  retrievalDemoConfirmed: boolean;
}) {
  const mutationResult = await kernelMutation<GatewayRouteReturnByName<"setup.recordActivationReceipt">>(
    "setup.recordActivationReceipt",
    {
      surfaceCapability: "chat-only",
      confirmedEntryCount,
      entriesAcrossCollections,
      retrievalDemoConfirmed,
    },
  );

  const result: RecordActivationResult = {
    activated: true,
    alreadyActivated: mutationResult.alreadyActivated,
    receiptId: mutationResult.receiptId ?? null,
  };

  const summary = mutationResult.alreadyActivated
    ? "Activation receipt already recorded — chat-only flow short-circuits."
    : "Activation recorded — chat-only flow complete.";

  return {
    content: textContent(summary),
    structuredContent: success(summary, result),
  };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `record_activation` tool
// retired here. handleRecordActivation stays live — orient.ts (the `orient`
// compound, action=record-activation) is its only remaining caller.
